import { Card, Collapse, Grid, Switch, Divider } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftSelect from "components/SoftComponents/SoftSelect";
import SoftTypography from "components/SoftComponents/SoftTypography";
import FormField from "template/layouts/pages/account/components/FormField";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import bksAPI from "services/api/bks";
import Swal from "sweetalert2";
import { TransitionGroup } from "react-transition-group";
import { useNavigate } from "react-router-dom";
import { useSoftUIController } from "hooks/context";
import { useAuth0 } from "@auth0/auth0-react";

function ClearwaysDeviceSettingsForm({ selected_device, sites, selected_group, selected_subgroup = null, groups }) {
    bksAPI.loadToken(useAuth0());
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions } = controller;

    const canDeviceEdit = userPermissions.includes('devices:edit');
    const showAdvancedDeviceLevel = userPermissions.includes('devices-advanced:view');
    const canAdvancedDeviceEdit = userPermissions.includes('devices-advanced:edit');

    const formRef = useRef(null);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [deviceData, setDeviceData] = useState({
        uuid: selected_device.uuid,
        name: selected_device.name,
        position: selected_device.position,
        active: selected_device.active,
        serial_number: selected_device.serial_number,
        site_id: selected_device.stop.site_id,
        organisation_id: selected_device.organisation_id,
        db_type: selected_device.db_type,
    });

    const [groupValue, setGroupValue] = useState(selected_group ? selected_group.group_id : null);
    const [subgroupValue, setSubgroupValue] = useState(selected_subgroup ? selected_subgroup.group_id : null);
    const [siteIdValue, setSiteIdValue] = useState(selected_device.stop.site_id == 'undefined' ? null : selected_device.stop.site_id);

    const [usesSubgroups, setUsesSubgroups] = useState((groupValue != null && groups.filter((group) => group.group_id == groupValue)[0].subgroups.length > 0));

    const defaultValues = {
        uuid: selected_device.uuid,
        name: selected_device.name,
        position: selected_device.position,
        active: selected_device.active,
        serial_number: selected_device.serial_number,
        site_id: selected_device.stop.site_id,
        organisation_id: selected_device.organisation_id,
        db_type: selected_device.db_type,
    }
    // console.log(defaultValues)

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm({
        defaultValues: defaultValues
    });

    const triggerFormSubmit = () => {
        formRef.current.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true })
        );
    }

    const { mutateAsync, isPending } = useMutation({
        mutationFn: (values) => bksAPI.updateDeviceData(values),
        onMutate: async () => {
            Swal.fire({
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Saving...</div>',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getDevice', selected_device.uuid, selectedOrg] });
            queryClient.invalidateQueries({ queryKey: ['getAllDevices', selectedOrg] });
            Swal.close();
            Swal.fire({
                icon: 'success',
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Saved!</div>',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        },
        onError: (error) => {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`,
                html: error.message ? `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${error.message}</div>` : `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">There was a problem saving your data. Ensure the input is unique.</div>`,
            });
        }
    })

    function submitForm(formData) {
        formData.active = deviceData.active;
        formData.site_id = siteIdValue;
        formData.group_id = subgroupValue != null ? subgroupValue : groupValue;
        formData.uuid = selected_device.uuid;
        mutateAsync(formData);
    }

    const triggerDeviceReboot = async () => {
        try {
            var response = await bksAPI.reboot(selected_device.uuid);
            if (!response) {
                throw Error;
            }

            await Swal.fire({
                icon: 'success',
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Device Rebooting!</div>',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        }
        catch (error) {
            Swal.close();
            console.log(error);
            Swal.fire({
                icon: "error",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`,
                text: "There was a problem rebooting the device.",
            });
        }
    };

    function updateValues(field, new_value) {
        // console.log(field, new_value)

        var new_data = deviceData;
        if (field == 'group') {
            setGroupValue(new_value);
            setSubgroupValue(null);
        }
        else if (field == 'subgroup') {
            setSubgroupValue(new_value);
        }
        else if (field == 'site_id') {
            setSiteIdValue(new_value);
        }
        else {
            new_data[field] = new_value;
        }
        // console.log(new_data);

        setDeviceData(new_data);
    }

    function applyValues(field) {
        return {
            "value": deviceData[field],
            "onChange": (new_value) => {
                updateValues(field, new_value.target.value);
            }
        }
    }

    // console.log(sites);
    if (sites.find(s => s.site_id == "Not Assigned") == null) {
        sites.push({ site_id: 'Not Assigned' })
    }

    useEffect(() => {
        setUsesSubgroups((groupValue != null && groups.filter((group) => group.group_id == groupValue)[0].subgroups.length > 0));
    }, [groupValue]);

    return (
        <SoftBox component="form" ref={formRef} onSubmit={handleSubmit((data) => { submitForm(data); })} style={{ height: deviceLayout == 'mobile' ? 'calc(100svh - 8.5rem - 16px)' : '100%' }}>
            <SoftBox display="flex" justifyContent="space-between" flexDirection="column" height="100%">
                <SoftBox>
                    <SoftTypography p={2} sx={{ background: '#f9f9f9' }} variant={deviceLayout == 'mobile' ? "h5" : "h4"}>Device Settings</SoftTypography>
                    <Divider />
                    <Grid container spacing={2} px={2} pt={2} pb={1}>
                        <Grid item xxs={12} md={6} xl={12} x4l={6}>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Device Name</SoftTypography>
                            </SoftBox>
                            <SoftBox>
                                <SoftInput placeholder="Device Name" {...register('name')} />
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12} md={6} xl={12} x4l={6}>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Serial Number</SoftTypography>
                            </SoftBox>
                            <SoftBox>
                                {showAdvancedDeviceLevel && canAdvancedDeviceEdit ? (
                                    <SoftInput placeholder="Serial Number" {...register('serial_number')} />
                                ) : (
                                    <SoftInput placeholder="Serial Number" {...register('serial_number')} disabled />
                                )}
                            </SoftBox>
                        </Grid>

                        <Grid item xxs={12} md={usesSubgroups ? 4 : 6} xl={6} x4l={usesSubgroups ? 4 : 6}>
                            <SoftBox>
                                <SoftBox pl={1} my={0.5}>
                                    <SoftTypography variant="body2" fontWeight='bold'>eMinor Group</SoftTypography>
                                </SoftBox>
                                <SoftBox>
                                    <Controller
                                        name="SoftSelectGroup"
                                        control={control}
                                        render={({ field }) => (
                                            <SoftSelect
                                                defaultValue={groupValue ? { value: groupValue, label: groups.filter((group) => group.group_id == groupValue)[0].name } : ''}
                                                options={groups.map(row => { return { value: row.group_id, target: { value: row.group_id }, label: row.name } })}
                                                onChange={(new_value) => { updateValues('group', new_value.target.value) }}

                                            />
                                        )}
                                    />
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                        {usesSubgroups && (
                            <Grid item xxs={12} md={4} xl={6} x4l={4}>
                                <TransitionGroup>
                                    {/* {usesSubgroups && ( */}
                                    <Collapse>
                                        <SoftBox>
                                            <SoftBox pl={1} my={0.5}>
                                                <SoftTypography variant="body2" fontWeight='bold'>eMinor Subgroup</SoftTypography>
                                            </SoftBox>
                                            {subgroupValue == null && (
                                                <SoftBox>
                                                    <Controller
                                                        name="SoftSelectSubgroup"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <SoftSelect
                                                                options={groups.filter((group) => group.group_id == groupValue)[0].subgroups.map(row => { return { value: row.group_id, target: { value: row.group_id }, label: row.name } })}
                                                                onChange={(new_value) => { updateValues('subgroup', new_value.target.value) }}

                                                            />
                                                        )}
                                                    />
                                                </SoftBox>
                                            )}
                                            {subgroupValue != null && (
                                                <SoftBox>
                                                    <Controller
                                                        name="SoftSelectSubgroup"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <SoftSelect
                                                                defaultValue={{ value: subgroupValue, label: groups.filter((group) => group.group_id == groupValue)[0].subgroups.filter((subgroup) => subgroup.group_id == subgroupValue)[0].name }}
                                                                options={groups.filter((group) => group.group_id == groupValue)[0].subgroups.map(row => { return { value: row.group_id, target: { value: row.group_id }, label: row.name } })}
                                                                onChange={(new_value) => { updateValues('subgroup', new_value.target.value) }}

                                                            />
                                                        )}
                                                    />
                                                </SoftBox>
                                            )}
                                        </SoftBox>
                                    </Collapse>
                                    {/* )} */}
                                </TransitionGroup>
                            </Grid>
                        )}
                        <Grid item xxs={12} md={usesSubgroups ? 4 : 6} xl={usesSubgroups ? 12 : 6} x4l={usesSubgroups ? 4 : 6}>
                            <TransitionGroup>
                                {((groupValue != null && groups.filter((group) => group.group_id == groupValue)[0].subgroups.length == 0) || (subgroupValue != null && groups.filter((group) => group.group_id == groupValue)[0].subgroups.length > 0)) && (
                                    <Collapse>
                                        <SoftBox>
                                            <SoftBox pl={1} my={0.5}>
                                                <SoftTypography variant="body2" fontWeight='bold'>Site ID</SoftTypography>
                                            </SoftBox>
                                            <SoftBox>
                                                <Controller
                                                    name="SoftSelectSiteID"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <SoftSelect
                                                            defaultValue={{ value: selected_device.stop.site_id, label: selected_device.stop.site_id }}
                                                            options={sites.filter((site) => site.site_id == 'Not Assigned' || (subgroupValue == null ? site.group_id == groupValue : site.group_id == subgroupValue)).sort((a, b) => a.site_id.localeCompare(b.site_id, 'en', { numeric: true })).map(row => { return { value: row.site_id, target: { value: row.site_id }, label: row.site_id } })}
                                                            onChange={(new_value) => { updateValues('site_id', new_value.target.value) }}
                                                            disabled={(groupValue != null && groups.filter((group) => group.group_id == groupValue)[0].subgroups.length == 0) || (subgroupValue != null && groups.filter((group) => group.group_id == groupValue)[0].subgroups.length > 0)}
                                                        />
                                                    )}
                                                />
                                            </SoftBox>
                                        </SoftBox>
                                    </Collapse>
                                )}
                            </TransitionGroup>
                        </Grid>


                        <Grid item xxs={12}>
                            <Grid container>
                                <Grid item xxs={12}>
                                    <SoftTypography variant="caption" fontWeight='bold'>Active</SoftTypography>
                                </Grid>
                                <Grid item xxs={12}>
                                    <SoftBox my={1.5}>
                                        <Controller
                                            name="checkbox"
                                            control={control}
                                            defaultValue={defaultValues.active}
                                            render={({ field }) => <Switch onChange={(e) => { field.onChange(e.target.checked); var new_data = deviceData; new_data['active'] = !new_data['active']; setDeviceData(new_data) }} checked={field.value == null ? defaultValues.active : field.value} />}
                                        />
                                    </SoftBox>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </SoftBox>

                <SoftBox>
                    <Divider />
                    <SoftBox sx={{ background: '#f9f9f9' }} display='flex' flexDirection='row' justifyContent='space-between' p={2}>
                        <SoftButton variant="gradient" color="dark" disabled={isPending} onClick={() => { triggerFormSubmit(); }}>Save</SoftButton>
                        {selected_device?.hardware_type.includes('bk') && (
                            <SoftButton variant="gradient" color="dark" onClick={() => { triggerDeviceReboot(); }}>Reboot Device</SoftButton>
                        )}
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </SoftBox >
    );
}

ClearwaysDeviceSettingsForm.propTypes = {
    selected_device: PropTypes.object,
    sites: PropTypes.array,
    selected_group: PropTypes.object,
    selected_subgroup: PropTypes.object,
    groups: PropTypes.array,
};

export default ClearwaysDeviceSettingsForm;